// ─────────────────────────────────────────────────────────────
//  turyna.xyz — ty-reveal scroll engine
//  Nahrazuje Minimog IntersectionObserver (ten nefunguje s
//  overflow-x: clip na body/html — rootMargin se ignoruje).
//
//  Dva módy:
//  1. WORD-SPLIT  — h1, h2, h3, .m-section__heading
//     → text se rozdělí na slova, každé animuje translateY(105%→0)
//  2. BLOK FADE   — .m-richtext__description, .m-section__subheading,
//                   .m-section__description, .lz-popis, .lz-meta,
//                   .cards-aside, .cards-card-desc, p[class*="ssf-end-text"]
//     → opacity 0→1 + translateY(16px→0)
//
//  Skip (nikdy neanimovat):
//  .m-header, nav, [role="navigation"], .m-cart, #CartDrawer,
//  .m-cart-drawer, .m-product-card, .m-breadcrumb, .m-pagination,
//  .ty-no-reveal, .t-hero-content, turyna-hero-rotator
// ─────────────────────────────────────────────────────────────

(function () {
  'use strict';

  // ── Reduced motion ───────────────────────────────────────────
  // Když uživatel nechce pohyb, engine prvky neskrývá ani neanimuje:
  // jen je zaregistruje a okamžitě odhalí (CSS @media navíc vynuluje
  // jakýkoli transform/transition). Žádný word-split → nadpisy zůstanou
  // jako čistý text včetně vnořených spanů.
  var _tyReduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── Selektory ────────────────────────────────────────────────

  var HEADING_SEL = 'h1, h2, h3, .m-section__heading';

  var TEXT_SEL = [
    '.m-section__subheading',
    '.m-section__description',
    '.lz-popis',
    '.lz-meta',
    '.cards-aside',
    '.cards-card-desc',
    'p[class*="ssf-end-text"]'
  ].join(', ');

  // Selektory pro kontejnery kde animujeme každý <p> zvlášť (stagger)
  var RICHTEXT_CONTAINERS = [
    '.m-richtext__description',
    '.about__block-text'
  ].join(', ');

  var SKIP_SEL = [
    '.m-header',
    'nav',
    '[role="navigation"]',
    '.m-cart',
    '#CartDrawer',
    '.m-cart-drawer',
    '.m-product-card',
    '.m-breadcrumb',
    '.m-pagination',
    '.ty-no-reveal',
    '.t-hero-content',
    'turyna-hero-rotator'
  ].join(', ');

  // ── State ────────────────────────────────────────────────────

  var _tyPending = new Set();

  // ── Helpers ──────────────────────────────────────────────────

  function _tyHasSkipAncestor(el) {
    var node = el.parentElement;
    while (node && node !== document.body) {
      try {
        if (node.matches(SKIP_SEL)) return true;
      } catch (e) {}
      node = node.parentElement;
    }
    return false;
  }

  function _tyHasStickyAncestor(el) {
    var node = el.parentElement;
    while (node && node !== document.body) {
      var st = window.getComputedStyle(node).position;
      if (st === 'sticky' || st === '-webkit-sticky') return true;
      node = node.parentElement;
    }
    return false;
  }

  function _tyInViewport(el) {
    var rect = el.getBoundingClientRect();
    var wh = window.innerHeight || document.documentElement.clientHeight;
    // Odhal prvek, jakmile jeho horní hrana přejde 92 % výšky viewportu.
    // Spodní mez ZÁMĚRNĚ není 'rect.bottom > 0', ale 'rect.top < wh*0.92':
    // prvek, který už je celý NAD oknem (rychlý scroll, skok na kotvu,
    // obnova scroll pozice), musí být taky považován za odhalený — jinak
    // by "proletěl" mezi dvěma scroll-callbacky a zůstal trvale opacity:0.
    return rect.top < wh * 0.92;
  }

  // ── Typografie: jednoznakovky ────────────────────────────────

  // Jednoznakovky (k, s, v, z, a, i, o, u) nesmí zůstat na konci řádku.
  // Stejné pravidlo jako globální hlídač v snippets/custom-code-body.liquid,
  // ale MUSÍ proběhnout PŘED word-splitem: split rozřeže text na .ty-word
  // spany a globální hlídač pak už mezery mezi slovy nevidí (textové uzly
  // obsahují jen samotná slova). Pevná mezera navíc drží dvojici
  // "i železná" v JEDNOM .ty-word boxu (split ji nerozdělí — viz
  // _tySplitNode), takže řádek se za jednoznakovkou nikdy nezlomí.
  var NBSP = ' ';
  var ORPHAN_RE = new RegExp(
    '(^|[\\s(„"‘’>–—-])([ksvzaiouKSVZAIOU])[ \\t]+(?=\\S)',
    'g'
  );

  function _tyFixOrphans(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      var text = node.nodeValue;
      if (!text || text.indexOf(' ') === -1) continue;
      var prev;
      // Opakovaně — sousední jednoznakovky se překrývají ("a v lese").
      do {
        prev = text;
        text = text.replace(ORPHAN_RE, function (_m, before, ch) {
          return before + ch + NBSP;
        });
      } while (text !== prev);
      if (text !== node.nodeValue) node.nodeValue = text;
    }
  }

  // ── Word-split ───────────────────────────────────────────────

  // Globální čítač slov v rámci jednoho nadpisu → per-word stagger delay.
  function _tyMakeWord(chunk, index) {
    var outer = document.createElement('span');
    outer.className = 'ty-word';
    var inner = document.createElement('span');
    inner.className = 'ty-word__inner';
    inner.textContent = chunk;
    // Stagger: každé další slovo o krok později. Hodnota se násobí
    // tokenem --ty-stagger-word v CSS calc, takže rytmus řídí design systém.
    inner.style.setProperty(
      '--ty-word-delay',
      'calc(' + index + ' * var(--ty-stagger-word))'
    );
    outer.appendChild(inner);
    return outer;
  }

  // Rekurzivní split: prochází strom, obaluje POUZE textové uzly do .ty-word.
  // Vnořené elementy (např. <span class="tph-accent-text">, <a>) se zachovají
  // a jejich text se splitne uvnitř nich → barevné spany i odkazy se animují
  // společně s ostatními slovy. NEPŘEPISUJE innerHTML celého nadpisu.
  function _tySplitNode(node, counter) {
    var children = Array.prototype.slice.call(node.childNodes);
    children.forEach(function (child) {
      if (child.nodeType === Node.TEXT_NODE) {
        var text = child.textContent;
        if (text.length === 0) return;
        var frag = document.createDocumentFragment();
        // Split na všem whitespace KROMĚ pevné mezery  : dvojice
        // spojená pevnou mezerou ("i železná") zůstane jedním chunkem
        // → jeden .ty-word box → řádek se uvnitř nikdy nezlomí.
        var chunks = text.split(/([^\S ]+)/);
        chunks.forEach(function (chunk) {
          if (chunk.length === 0) return;
          if (/^\s+$/.test(chunk)) {
            // Mezera zůstává normálním textovým uzlem (žádný box) —
            // jednotnou výšku selectu řeší CSS přes overflow:visible na
            // .ty-word po dokončení reveal.
            frag.appendChild(document.createTextNode(chunk));
          } else {
            frag.appendChild(_tyMakeWord(chunk, counter.i));
            counter.i++;
          }
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        // Už obalené slovo nepřeskupuj (idempotence při re-scanu).
        if (child.classList && child.classList.contains('ty-word')) return;
        _tySplitNode(child, counter);
      }
    });
  }

  function _tySplitWords(el) {
    _tySplitNode(el, { i: 0 });
  }

  // ── Reveal ───────────────────────────────────────────────────

  // Po dokončení výjezdu slov zruš masku (overflow:hidden) → třída ty-reveal--done.
  // DŮLEŽITÉ: maska NESMÍ zmizet při ty-reveal--visible (to animaci spouští) —
  // jinak je slovo rovnou vidět na finální pozici a výjezd se neukáže. Masku
  // proto rušíme až tady, po transitionend posledního jezdce (s časovým
  // fallbackem, kdyby transitionend nepřišel).
  function _tyMarkDone(el) {
    if (el.classList.contains('ty-reveal--done')) return;
    el.classList.add('ty-reveal--done');
  }

  function _tyScheduleDone(el) {
    var words = el.querySelectorAll('.ty-word__inner');
    if (!words.length) {
      // Není word-split (fade blok) → dokončení po jeho transition.
      _tyMarkDone(el);
      return;
    }
    var last = words[words.length - 1];
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      _tyMarkDone(el);
    }
    last.addEventListener('transitionend', finish, { once: true });
    // Fallback: doba slow přechodu + per-word stagger (počet slov) + rezerva.
    setTimeout(finish, 900 + words.length * 60);
  }

  function _tyRevealEl(el) {
    if (_tyReduceMotion) {
      el.classList.add('ty-reveal--visible', 'ty-reveal--done');
      _tyPending.delete(el);
      return;
    }
    // double rAF pro spolehlivé spuštění přechodu
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.classList.add('ty-reveal--visible');
        _tyPending.delete(el);
        _tyScheduleDone(el);
      });
    });
  }

  // ── Registrace ───────────────────────────────────────────────

  function _tyRegisterHeading(el) {
    if (el.classList.contains('ty-registered')) return;
    if (_tyHasSkipAncestor(el)) return;
    // Přeskoč nadpisy bez reálného textu (jen whitespace / &nbsp; spacer).
    // Takové dekorativní prázdné nadpisy nemá smysl animovat ani splittovat.
    if (!/\S/.test((el.textContent || '').replace(/ /g, ' '))) return;
    el.classList.add('ty-registered', 'ty-reveal');
    // Typografie PŘED splitem: jednoznakovky sváže pevnou mezerou,
    // takže je split nechá pohromadě s následujícím slovem.
    _tyFixOrphans(el);
    // Při reduced-motion neděláme word-split — necháme nadpis jako čistý
    // text (vnořené spany/odkazy zůstanou nedotčené). CSS @media navíc
    // všechno zviditelní. Prvek jen zaregistrujeme, ať ho _tyCheckAll odhalí.
    if (!_tyReduceMotion) {
      _tySplitWords(el);
    }
    _tyPending.add(el);
  }

  function _tyRegisterText(el) {
    if (el.classList.contains('ty-registered')) return;
    if (_tyHasSkipAncestor(el)) return;
    el.classList.add('ty-registered', 'ty-reveal', 'ty-reveal--text');
    _tyPending.add(el);
  }

  // Richtext kontejner — každý <p> animuj zvlášť se staggerem
  function _tyRegisterRichtext(container) {
    if (container.classList.contains('ty-registered')) return;
    if (_tyHasSkipAncestor(container)) return;
    container.classList.add('ty-registered'); // dedup guard, ale ne ty-reveal
    var ps = container.querySelectorAll('p, li');
    if (ps.length === 0) {
      // žádné p uvnitř — animuj celý kontejner jako blok
      _tyRegisterText(container);
      return;
    }
    ps.forEach(function(p, i) {
      if (p.classList.contains('ty-registered')) return;
      if (p.textContent.trim().length === 0) return;
      p.classList.add('ty-registered', 'ty-reveal', 'ty-reveal--text');
      // stagger: 80ms na každý řádek
      p.style.transitionDelay = (i * 0.08) + 's';
      _tyPending.add(p);
    });
  }

  // ── Check loop ───────────────────────────────────────────────

  function _tyCheckAll(isInitial) {
    _tyPending.forEach(function (el) {
      // Při inicializaci přeskoč prvky ve sticky kontejneru
      if (isInitial && _tyHasStickyAncestor(el)) return;
      if (_tyInViewport(el)) {
        _tyRevealEl(el);
      }
    });
  }

  // ── Scan DOM ─────────────────────────────────────────────────

  function _tyScan(root) {
    root = root || document;

    // Headings
    var headings = root.querySelectorAll(HEADING_SEL);
    headings.forEach(function (el) {
      _tyRegisterHeading(el);
    });

    // Text blocks
    var texts = root.querySelectorAll(TEXT_SEL);
    texts.forEach(function (el) {
      _tyRegisterText(el);
    });

    // Richtext kontejnery — animuj každý <p> zvlášť
    var richtexts = root.querySelectorAll(RICHTEXT_CONTAINERS);
    richtexts.forEach(function (el) {
      _tyRegisterRichtext(el);
    });
  }

  // ── Scroll listener (throttled) ──────────────────────────────

  var _tyScrollScheduled = false;

  function _tyOnScroll() {
    if (_tyScrollScheduled) return;
    _tyScrollScheduled = true;
    requestAnimationFrame(function () {
      _tyScrollScheduled = false;
      _tyCheckAll(false);
    });
  }

  // ── Init ─────────────────────────────────────────────────────

  function _tyInit() {
    _tyScan(document);
    _tyCheckAll(true);  // okamžitě odhal co je vidět (sticky fix zapnut)

    window.addEventListener('scroll', _tyOnScroll, { passive: true });
    window.addEventListener('resize', _tyOnScroll, { passive: true });
  }

  // ── Shopify design mode (preview editor) ─────────────────────

  function _tyOnSectionLoad(event) {
    var root = event.target || document;
    _tyScan(root);
    // V design mode vše okamžitě odhal (bez animace)
    _tyPending.forEach(function (el) {
      el.classList.add('ty-reveal--visible');
    });
    _tyPending.clear();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', _tyOnSectionLoad);
    document.addEventListener('shopify:section:reorder', function () {
      _tyScan(document);
    });
  }

  // ── Exponuj globálně pro debug ────────────────────────────────

  window._tyPending   = _tyPending;
  window._tyCheckAll  = _tyCheckAll;
  window._tyRevealEl  = _tyRevealEl;
  window._tyScan      = _tyScan;

  // ── Start ─────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _tyInit);
  } else {
    _tyInit();
  }

})();

// ─────────────────────────────────────────────────────────────
//  Minimog scroll trigger (zachováno pro kompatibilitu
//  s Minimog sekcemi které používají m-scroll-trigger třídu)
// ─────────────────────────────────────────────────────────────

var SCROLL_ANIMATION_TRIGGER_CLASSNAME    = 'm-scroll-trigger';
var SCROLL_ANIMATION_OFFSCREEN_CLASSNAME  = 'm-scroll-trigger--offscreen';
var SCROLL_ANIMATION_CANCEL_CLASSNAME     = 'm-scroll-trigger--cancel';

function updateElementVisibility(element, isVisible) {
  if (isVisible) {
    element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
  } else {
    element.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    element.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
  }
}

function handleMobileChange(element) {
  var removeTriggerClass = function () {
    element.classList.remove(SCROLL_ANIMATION_TRIGGER_CLASSNAME);
  };
  if (
    (typeof MinimogTheme !== 'undefined' && MinimogTheme.config && MinimogTheme.config.mqlMobile) ||
    element.dataset.type === 'fixed'
  ) {
    removeTriggerClass();
  }
  document.addEventListener('matchMobile', removeTriggerClass);
  document.addEventListener('unmatchMobile', removeTriggerClass);
}

function onIntersection(entries, observer) {
  entries.forEach(function (entry, index) {
    var isIntersecting = entry.isIntersecting;
    var target = entry.target;
    updateElementVisibility(target, isIntersecting);
    if (isIntersecting) {
      if (target.hasAttribute('data-cascade')) {
        target.setAttribute('style', '--animation-order: ' + index + ';');
      }
      observer.unobserve(target);
    }
    if (target.classList.contains('m-sidebar')) {
      handleMobileChange(target);
    }
  });
}

var _minimogObserver = new IntersectionObserver(onIntersection, {
  rootMargin: '0px 0px -50px 0px'
});

function initializeScrollAnimationTrigger(rootEl, isDesignModeEvent) {
  rootEl = rootEl || document;
  isDesignModeEvent = isDesignModeEvent || false;
  var elements = rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME);
  if (elements.length === 0 || isDesignModeEvent) return;
  Array.from(elements).forEach(function (element) {
    _minimogObserver.observe(element);
  });
}

document.addEventListener('DOMContentLoaded', function () {
  initializeScrollAnimationTrigger();
});

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    initializeScrollAnimationTrigger(event.target, true);
  });
  document.addEventListener('shopify:section:reorder', function () {
    initializeScrollAnimationTrigger(document, true);
  });
}
